import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import Input from "../../../components/elements/form/input";
import Button from '../../../components/elements/button';
import toast from 'react-hot-toast';
import { useAppLoader } from '../../../_common/pageLoader/appLoaderHook';
import { useFacilitiesApi } from '../../../store/api/hooks/facilitiesApiHook';
import { useBillComApi } from '../../../store/api/hooks/billComApiHook';
import SelectBox from '../../../components/elements/form/selectBox';

interface DepartmentFormProps {
    _ref?: any;
    editData?: any;
    onCreate?: () => void;
    onCancel?: () => void;
}

type DepartmentFormValueType = {
    departmentName: string;
    billComDepartmentID?: any;
};

const CreateMasterDepartment: React.FC<DepartmentFormProps> = ({ _ref, editData, onCreate = () => { }, onCancel = () => { } }) => {
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<DepartmentFormValueType>({
        defaultValues: {
            departmentName: '',
            billComDepartmentID: null,
        }
    });

    const { showLoader, hideLoader } = useAppLoader();
    const facilityApi = useFacilitiesApi();
    const billComApi = useBillComApi();
    const [billComDepartmentOptions, setBillComDepartmentOptions] = useState<any[]>([]);

    const formReset = () => {
        reset({
            departmentName: '',
            billComDepartmentID: null,
        });
    };

    const onSubmit: SubmitHandler<DepartmentFormValueType> = formData => {
        showLoader();
        
        const payload = {
            name: formData.departmentName,
            billComDepartmentID: formData.billComDepartmentID?.value || null,
            ...(editData && { departmentID: editData.departmentID })
        };

        facilityApi.saveDepartment(
            payload,
            (response: any) => {
                hideLoader();
                const action = editData ? 'updated' : 'created';
                toast.success(`Department ${action} successfully`);
                onCreate();
                onCancel();
                formReset();
            },
            (error: any) => {
                hideLoader();
                toast.error(error || 'Failed to save Department');
            }
        );
    };

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.reset = formReset;
        }
    }, [_ref]);

    useEffect(() => {
        if (editData == null) {
            formReset();
        }
    }, [editData]);

    useEffect(() => {
        if (editData && billComDepartmentOptions.length > 0) {
            setValue('departmentName', editData.departmentName || '');
            
            // Find matching option for SelectBox
            const selectedDept = billComDepartmentOptions.find(opt => opt.value === editData.billComDepartmentID);
            setValue('billComDepartmentID', selectedDept || null);
        } else if (editData) {
            setValue('departmentName', editData.departmentName || '');
            setValue('billComDepartmentID', null);
        } else {
            formReset();
        }
    }, [editData, billComDepartmentOptions]);

    useEffect(() => {
        // Fetch Bill.com departments
        const fetchBillComDepartments = () => {
            const payload = {
                pageSize: 1000,
                pageNumber: 1,
                isLinked: 0,
            };

            billComApi.getBillComDepartments(
                payload,
                (response: any) => {
                    if (response?.results) {
                        const options = response.results.map((dept: any) => ({
                            label: dept.name,
                            value: dept.id
                        }));
                        setBillComDepartmentOptions(options);
                    }
                },
                (error: any) => {
                    console.error('Failed to fetch Bill.com departments:', error);
                }
            );
        };

        fetchBillComDepartments();
    }, []);

    return (
        <form onSubmit={handleSubmit(onSubmit)} ref={_ref} className="flex flex-col h-full">
            <div className="flex-1 overflow-auto">
                <Controller
                    name="departmentName"
                    control={control}
                    rules={{ required: "Department Name is required" }}
                    render={({ field: { value, onChange } }) => (
                        <Input
                            label="Department Name"
                            type="text"
                            value={value}
                            onChange={onChange}
                            required
                            error={errors.departmentName?.message}
                        />
                    )}
                />
            
                <Controller
                    name="billComDepartmentID"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <SelectBox
                            label="Bill.com Department Name"
                            value={value}
                            onChange={onChange}
                            options={billComDepartmentOptions}
                            placeholder="Select Bill.com Department"
                        />
                    )}
                />
            </div>

            <div className="flex justify-end gap-2 flex-shrink-0 pt-2 border-t border-gray-200">
                <Button type="button" theme="secondary" onClick={formReset}>
                    Reset
                </Button>
                <Button type="submit">
                    Save
                </Button>
            </div>
        </form>
    );
};

export default CreateMasterDepartment;